import { ReactElement, ReactNode, useEffect } from 'react'
import produce from 'immer'
import { cloneDeep } from 'lodash-es'
import create from 'zustand'
import { combine } from 'zustand/middleware'
import { validate } from '../middleware'
import { augmentProposal } from './utils'

interface Props {
  children: ReactNode
}

interface StructuralPlanningInitialState {
  proposal: StiffeningSegment[]
  selectedIds: Set<string>
  upperMostCeilingNotStiffening: boolean
  horizontalDistributionSettings: HoirzontalDistributionSettings | null

  hiddenLoads: string[]
  windLoadDirection: WindDirection
}

interface StructuralPlanningStoreType extends StructuralPlanningInitialState {
  setProposal: (data: StiffeningSegment[]) => void
  setSelectedIds: (selectedIds: string[]) => void
  addSelectedIds: (selectedIds: string[]) => void
  deselectId: (id: string) => void
  deselectAllIds: () => void
  assignStiffeningIntervals: (ids: Set<string>, stiffening: StiffeningElementStiffening) => void
  setUpperMostCeilingNotStiffening: (upperMostCeilingStiffening: boolean) => void
  setHorizontalDistributionSettings: (
    horizontalDistributionSettings: HoirzontalDistributionSettings,
  ) => void

  clear: () => void
  setHiddenLoads: (loads: string[]) => void
  clearHiddenLoads: () => void

  addRip: (rip: Rip) => void
  removeRip: (guid: string) => void
  addLintel: (lintel: Lintel) => void

  updateRip: (rip: Rip) => void

  setWindLoadDirection: (direction: WindDirection) => void
}

const initialState: StructuralPlanningInitialState = {
  proposal: [],
  selectedIds: new Set(),
  upperMostCeilingNotStiffening: true,
  horizontalDistributionSettings: null,

  hiddenLoads: [],
  windLoadDirection: 'Direction0',
}

const createStore = () =>
  create<StructuralPlanningStoreType>(
    validate(
      combine(cloneDeep(initialState), set => ({
        clear: () => set(cloneDeep(initialState)),

        setProposal: (data: StiffeningSegment[]) =>
          set(
            produce(state => {
              state.proposal = augmentProposal(data)
            }),
          ),

        assignStiffeningIntervals: (ids: Set<string>, stiffening: StiffeningElementStiffening) =>
          set(
            produce(state => {
              state.proposal.forEach((interval: StiffeningSegment) => {
                if (ids.has(interval.localId as string)) interval.stiffening = stiffening
              })
            }),
          ),

        addSelectedIds: (selectedIds: string[]) =>
          set(
            produce(state => {
              state.selectedIds = new Set([...state.selectedIds, ...selectedIds])
            }),
          ),

        setSelectedIds: (selectedIds: string[]) =>
          set(
            produce(state => {
              state.selectedIds = new Set([...selectedIds])
            }),
          ),

        deselectId: (id: string) =>
          set(
            produce(state => {
              state.selectedIds.delete(id)
            }),
          ),

        deselectAllIds: () =>
          set(
            produce(state => {
              state.selectedIds = new Set()
            }),
          ),

        setUpperMostCeilingNotStiffening: (upperMostCeilingNotStiffening: boolean) =>
          set({ upperMostCeilingNotStiffening }),

        setHorizontalDistributionSettings: (
          horizontalDistributionSettings: HoirzontalDistributionSettings,
        ) => set({ horizontalDistributionSettings }),

        setHiddenLoads: (hiddenLoads: string[]) => set({ hiddenLoads }),

        clearHiddenLoads: () => set({ hiddenLoads: [] }),

        addLintel: (lintel: Lintel) =>
          set(
            produce(state => {
              state.model.lintels.push(lintel)
            }),
          ),

        setWindLoadDirection: (direction: WindDirection) =>
          set(
            produce(state => {
              state.windLoadDirection = direction
            }),
          ),
      })),
    ),
  )

const useStructuralPlanningStore = createStore()

const StructuralPlanningStoreProvider = ({ children }: Props): ReactElement => {
  const clear = useStructuralPlanningStore(state => state.clear)

  useEffect(() => {
    return () => clear()
  }, [])

  return <>{children}</>
}

export { StructuralPlanningStoreProvider, useStructuralPlanningStore }
